import { TriangleAlert } from "lucide-react";
import type { WriteRejection } from "@estelle/domain";

import { Button } from "@/components/ui/button";
import { describeRejection } from "@/modules/catalog/writeRejection";

/**
 * Every `WriteRejection` kind rendered as what it actually is, shared by the
 * HSN editor, the product edit form and the bulk edit report (#170) — same
 * shape as `modules/orders/screens/RejectionNotice.tsx`.
 *
 * `conflict` gets bespoke treatment because it carries structured data a
 * user can act on — what changed in Shopify and a reload affordance — that a
 * flattened string would throw away. `invalid` shows its per-field messages.
 * `blocked` and `channel_rejected` carry the channel's own words verbatim,
 * and `forbidden` cannot occur from these actions today, but the union has
 * no `default`: a new kind fails this file at compile time instead of
 * silently rendering nothing.
 */
export function RejectionNotice({
  rejection,
  onReload,
}: {
  rejection: WriteRejection;
  onReload: () => void;
}) {
  if (rejection.kind === "conflict") {
    return (
      <div
        role="alert"
        className="border-destructive/30 bg-destructive/5 text-destructive flex items-start gap-1.5 rounded-md border p-2 text-xs"
      >
        <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
        <div className="min-w-0 space-y-1.5">
          <p>{rejection.message}</p>
          {rejection.changes && rejection.changes.length > 0 ? (
            <dl className="text-foreground grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
              {rejection.changes.map((change) => (
                <div key={change.field} className="contents">
                  <dt className="text-muted-foreground">{change.field}</dt>
                  <dd className="break-words">
                    <span className="text-muted-foreground line-through">
                      {change.loaded ?? "—"}
                    </span>{" "}
                    → {change.current ?? "—"}
                  </dd>
                </div>
              ))}
            </dl>
          ) : null}
          <Button type="button" size="xs" variant="outline" onClick={onReload}>
            Reload
          </Button>
        </div>
      </div>
    );
  }

  if (rejection.kind === "invalid") {
    return (
      <p role="alert" className="text-destructive text-xs">
        {rejection.message}
        {rejection.fields
          ? " " +
            Object.entries(rejection.fields)
              .map(([field, problem]) => `${field}: ${problem}`)
              .join("; ")
          : null}
      </p>
    );
  }

  return (
    <div
      role="alert"
      className="border-destructive/30 bg-destructive/5 text-destructive flex items-start gap-1.5 rounded-md border p-2 text-xs"
    >
      <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
      <div className="min-w-0 space-y-0.5">
        {rejection.kind === "channel_rejected" ? (
          <p className="font-medium">
            Shopify refused this change. Nothing was changed.
          </p>
        ) : null}
        <p className="break-words">{describeRejection(rejection)}</p>
      </div>
    </div>
  );
}
